import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ScopeList — renders consent scope items.
 *
 * Legacy mode (groupMode absent or false):
 *   Groups scopes by `category` — same rendering as before.
 *
 * Group mode (groupMode={true}):
 *   Groups scopes by `consentGroup`, renders collapsible sections per group,
 *   shows mutating-action warnings for scopes with mutating=true,
 *   and shows a badge on any group that contains at least one mutating scope.
 *   Scopes with no consentGroup fall into an "Other" catch-all group.
 */
import { useRef, useEffect, useState } from 'react';
import { CONSENT_GROUPS } from './consent-groups.js';
function slugify(name) {
    return name.replace(/[^a-zA-Z0-9]/g, '-');
}
// ── Mutating warning styles using CSS custom properties with fallbacks ──────
const mutatingWarningStyle = {
    backgroundColor: 'var(--consent-warning-bg, #fef3c7)',
    borderLeft: '3px solid var(--consent-warning-border, #d97706)',
    color: 'var(--consent-warning-text, #92400e)',
    fontSize: '0.75rem',
    padding: '0.375rem 0.5rem',
    marginTop: '0.25rem',
    borderRadius: '0.25rem',
};
function GroupCheckbox({ checked, indeterminate, disabled, label, onChange }) {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current) {
            ref.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);
    return (_jsx("input", { ref: ref, type: "checkbox", checked: checked, disabled: disabled, "aria-label": `Select all tools in ${label}`, onChange: (e) => onChange(e.target.checked), style: { marginRight: '0.5rem', flexShrink: 0 } }));
}
function GroupSection({ groupKey, label, groupScopes, selectedScopes, onToggle, disabled }) {
    const [expanded, setExpanded] = useState(true);
    const selectedInGroup = groupScopes.filter((s) => selectedScopes.includes(s.name));
    const allChecked = selectedInGroup.length === groupScopes.length;
    const someChecked = selectedInGroup.length > 0 && !allChecked;
    const hasAnyMutating = groupScopes.some((s) => s.mutating === true);
    function handleGroupChange(checked) {
        groupScopes.forEach((scope) => {
            const isCurrentlySelected = selectedScopes.includes(scope.name);
            if (checked && !isCurrentlySelected) {
                onToggle(scope.name, true);
            }
            else if (!checked && isCurrentlySelected) {
                onToggle(scope.name, false);
            }
        });
    }
    return (_jsxs("section", { "data-testid": `consent-group-${groupKey}`, "aria-labelledby": `consent-group-label-${groupKey}`, style: { marginBottom: '0.75rem', border: '1px solid var(--consent-border, #e5e7eb)', borderRadius: '0.375rem' }, children: [_jsxs("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: 'var(--consent-group-header-bg, #f9fafb)',
                    borderRadius: expanded ? '0.375rem 0.375rem 0 0' : '0.375rem',
                    borderBottom: expanded ? '1px solid var(--consent-border, #e5e7eb)' : 'none',
                }, children: [_jsx(GroupCheckbox, { checked: allChecked, indeterminate: someChecked, disabled: disabled, label: label, onChange: handleGroupChange }), _jsx("span", { id: `consent-group-label-${groupKey}`, style: { fontWeight: 600, fontSize: '0.875rem', flex: 1 }, children: label }), hasAnyMutating && (_jsx("span", { "data-testid": `consent-group-mutating-badge-${groupKey}`, role: "img", "aria-label": "Contains actions that modify data", style: {
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: 'var(--consent-warning-text, #92400e)',
                            backgroundColor: 'var(--consent-warning-bg, #fef3c7)',
                            border: '1px solid var(--consent-warning-border, #d97706)',
                            borderRadius: '0.25rem',
                            padding: '0.125rem 0.375rem',
                        }, children: "\u26A0 Modifies data" })), _jsx("button", { type: "button", "aria-expanded": expanded, "aria-controls": `consent-group-scopes-${groupKey}`, "aria-label": expanded ? `Collapse ${label}` : `Expand ${label}`, onClick: () => setExpanded((v) => !v), style: {
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.125rem 0.25rem',
                            fontSize: '0.75rem',
                            color: 'var(--consent-muted, #6b7280)',
                            lineHeight: 1,
                        }, children: expanded ? '▲' : '▼' })] }), expanded && (_jsx("ul", { id: `consent-group-scopes-${groupKey}`, style: { listStyle: 'none', padding: '0.25rem 0.75rem', margin: 0 }, children: groupScopes.map((scope) => {
                    const scopeSlug = slugify(scope.name);
                    const checkboxId = `scope-checkbox-${scopeSlug}`;
                    const isChecked = selectedScopes.includes(scope.name);
                    return (_jsxs("li", { "data-testid": `consent-scope-item-${scopeSlug}`, style: { padding: '0.25rem 0' }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }, children: [_jsx("input", { id: checkboxId, type: "checkbox", checked: isChecked, disabled: disabled, "aria-label": `Grant permission: ${scope.displayName}`, onChange: (e) => onToggle(scope.name, e.target.checked), style: { marginTop: '0.2rem', flexShrink: 0 } }), _jsxs("label", { htmlFor: checkboxId, style: { cursor: disabled ? 'not-allowed' : 'pointer' }, children: [_jsx("span", { style: { fontWeight: 500, display: 'block', fontSize: '0.875rem' }, children: scope.displayName }), _jsx("span", { style: { color: 'var(--consent-muted, #6b7280)', fontSize: '0.75rem' }, children: scope.description })] })] }), scope.mutating === true && (_jsx("div", { "data-testid": `consent-scope-mutating-warning-${scopeSlug}`, role: "alert", style: mutatingWarningStyle, children: "This action can modify your data. Review carefully before approving." }))] }, scope.name));
                }) }))] }));
}
// ── Legacy rendering (category-grouped, same as original) ───────────────────
function renderLegacy(scopes, selectedScopes, onToggle, disabled) {
    const grouped = scopes.reduce((acc, scope) => {
        const cat = scope.category || 'Other';
        if (!acc[cat])
            acc[cat] = [];
        acc[cat].push(scope);
        return acc;
    }, {});
    return (_jsx("div", { role: "group", "aria-label": "Requested permissions", children: Object.entries(grouped).map(([category, categoryScopes]) => (_jsxs("section", { "aria-labelledby": `scope-cat-${slugify(category)}`, children: [_jsx("h3", { id: `scope-cat-${slugify(category)}`, style: { fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem', marginTop: '0.75rem' }, children: category }), _jsx("ul", { style: { listStyle: 'none', padding: 0, margin: 0 }, children: categoryScopes.map((scope) => {
                        const scopeSlug = slugify(scope.name);
                        const checkboxId = `scope-checkbox-${scopeSlug}`;
                        const isChecked = selectedScopes.includes(scope.name);
                        return (_jsxs("li", { "data-testid": `consent-scope-item-${scopeSlug}`, style: { display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.25rem 0' }, children: [_jsx("input", { id: checkboxId, type: "checkbox", checked: isChecked, disabled: disabled, "aria-label": `Grant permission: ${scope.displayName}`, onChange: (e) => onToggle(scope.name, e.target.checked), style: { marginTop: '0.2rem', flexShrink: 0 } }), _jsxs("label", { htmlFor: checkboxId, style: { cursor: disabled ? 'not-allowed' : 'pointer' }, children: [_jsx("span", { style: { fontWeight: 500, display: 'block', fontSize: '0.875rem' }, children: scope.displayName }), _jsx("span", { style: { color: 'var(--consent-muted, #6b7280)', fontSize: '0.75rem' }, children: scope.description })] })] }, scope.name));
                    }) })] }, category))) }));
}
// ── Group-mode rendering ─────────────────────────────────────────────────────
function renderGrouped(scopes, selectedScopes, onToggle, disabled) {
    // Group scopes by consentGroup; ungrouped fall into "other"
    const grouped = scopes.reduce((acc, scope) => {
        const key = scope.consentGroup ?? 'other';
        if (!acc[key])
            acc[key] = [];
        acc[key].push(scope);
        return acc;
    }, {});
    // Sort groups so connection_basics always renders first, then other registry-ordered groups, then "other"
    const sortedGroupEntries = Object.entries(grouped).sort(([keyA], [keyB]) => {
        if (keyA === 'connection_basics')
            return -1;
        if (keyB === 'connection_basics')
            return 1;
        if (keyA === 'other')
            return 1;
        if (keyB === 'other')
            return -1;
        return 0;
    });
    return (_jsx("div", { role: "group", "aria-label": "Requested permissions", children: sortedGroupEntries.map(([key, groupScopes]) => {
            const registryEntry = CONSENT_GROUPS[key];
            const label = registryEntry?.label ?? 'Other';
            return (_jsx(GroupSection, { groupKey: key, label: label, groupScopes: groupScopes, selectedScopes: selectedScopes, onToggle: onToggle, disabled: disabled }, key));
        }) }));
}
// ── Public component ─────────────────────────────────────────────────────────
export function ScopeList({ scopes, selectedScopes, onToggle, disabled, groupMode }) {
    if (groupMode === true) {
        return renderGrouped(scopes, selectedScopes, onToggle, disabled);
    }
    return renderLegacy(scopes, selectedScopes, onToggle, disabled);
}
//# sourceMappingURL=ScopeList.js.map